import { ModalDialog } from "@/components/data-display/modal-dialog";
import { Input } from "@/components/inputs/input";
import { Button } from "@/components/ui/button";
import { createRef, useEffect } from "react";
import { CopyButton } from "@/components/copy-button";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { copyToClipboard } from "@/utils/copy-to-clipboard";
import { createToast } from "vercel-toast";

interface Vision360ProposalLinksModalProps {
  open: boolean;
  onClose: () => void;
  proposalLongUrl: string;
  proposalShortUrl: string;
  privateRoomsIds: string[];
  dealId?: string;
}

export function Vision360ProposalLinksModal({
  open,
  onClose,
  proposalLongUrl,
  proposalShortUrl,
  privateRoomsIds,
  dealId,
}: Vision360ProposalLinksModalProps) {
  const longUrlRef = createRef<HTMLInputElement>();
  const shortUrlRef = createRef<HTMLInputElement>();

  const handleCopyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        createToast("Copiado com sucesso", {
          type: "success",
          timeout: 2000,
        });
      } else {
        copyToClipboard(text);
      }
    } catch (err) {
      console.error("Erro ao copiar:", err);
      createToast("Erro ao copiar", {
        type: "error",
        timeout: 2000,
      });
    }
  };

  useEffect(() => {
    if (open) {
      sendTrackDataServer({
        url: window.location.pathname,
        eventName: "VISION_CLICK_PRIVATEOFFICE_PROPOSAL_FINAL",
        productType: "PO",
        data: {
          deal_id: dealId ?? null,
          private_office_ids: privateRoomsIds,
          proposalLongUrl,
        },
      });
    }
  }, [open, proposalLongUrl, privateRoomsIds, dealId]);

  return (
    <ModalDialog
      type="submit"
      title="Sucesso!"
      description={`Sua proposta foi criada com sucesso! Copie os links abaixo para compartilhar.`}
      open={open}
      handleClose={onClose}
      handleConfirm={() => {}}
      maxMidth="p-0 max-w-[404px]"
      showFooter={false}
      enableButton={true}
      titleStyle="text-xl pt-0 px-6 pb-0 zinc-950 text-center"
      descriptionStyle="text-sm px-6 text-zinc-500 mt-0 text-center"
      headerStyle="text-center items-center justify-center"
      image="/images/ConfirmedIcon.svg"
      imageStyle="pt-6"
    >
      <div className="pt-0 pb-6">
        <div className="px-6 pb-6">
          <div className="flex flex-col gap-3">
            <div className="flex gap-3 justify-end items-end">
              <div className="flex-1">
                <Input
                  ref={longUrlRef}
                  className="w-full flex-initial"
                  label="URL Longa"
                  name="long_url"
                  type="string"
                  showlabel={true}
                  defaultValue={proposalLongUrl}
                  readOnly
                />
              </div>
              <CopyButton
                size="w-[40px] h-[40px]"
                action={async () => {
                  if (longUrlRef.current) {
                    longUrlRef.current.select();
                    await handleCopyToClipboard(longUrlRef.current.value);
                  }
                }}
              />
            </div>

            <div className="flex gap-3 justify-end items-end">
              <div className="flex-1">
                <Input
                  ref={shortUrlRef}
                  className="w-full flex-initial"
                  label="URL Encurtada"
                  name="short_url"
                  type="string"
                  showlabel={true}
                  defaultValue={proposalShortUrl}
                  readOnly
                />
              </div>
              <CopyButton
                size="w-[40px] h-[40px]"
                action={async () => {
                  if (shortUrlRef.current) {
                    shortUrlRef.current.select();
                    await handleCopyToClipboard(shortUrlRef.current.value);
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className="border-t border-zinc-200 px-6 pt-6">
          <div className="flex flex-col">
            <Button type="button" className="min-h-[40px]" onClick={onClose}>
              Entendido
            </Button>
          </div>
        </div>
      </div>
    </ModalDialog>
  );
}
